import React, { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { USE_CASES, FUNCTIONS, STATUSES } from '../../mock';
import { useI18n } from '../../i18n';
import { BarRow } from '../charts';
import StatusBadge from '../StatusBadge';

export default function UseCases() {
  const { t, tf } = useI18n();
  const [q, setQ] = useState('');
  const [fn, setFn] = useState('all');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    return USE_CASES.filter((u) => {
      if (fn !== 'all' && u.function !== fn) return false;
      if (status !== 'all' && u.status !== status) return false;
      if (q && !u.title.toLowerCase().includes(q.toLowerCase()) && !u.id.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [q, fn, status]);

  const statusMeta = (k) => STATUSES.find((s) => s.key === k);
  const fnMeta = (k) => FUNCTIONS.find((f) => f.key === k);

  return (
    <div className="space-y-5 animate-fade-up">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchPlaceholder')}
            className="w-full h-11 ps-9 pe-3 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition" />
        </div>
        <select value={fn} onChange={(e) => setFn(e.target.value)}
          className="h-11 px-3 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:border-indigo-400 cursor-pointer">
          <option value="all">{t('allFunctions')}</option>
          {FUNCTIONS.map((f) => <option key={f.key} value={f.key}>{tf(f)}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-11 px-3 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:border-indigo-400 cursor-pointer">
          <option value="all">{t('allStatuses')}</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{tf(s)}</option>)}
        </select>
      </div>

      <div className="text-sm text-slate-500">{filtered.length} {t('resultsFound')}</div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((u) => {
          const sm = statusMeta(u.status);
          return (
            <button key={u.id} onClick={() => setSelected(u)}
              className="text-start bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-indigo-200 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-slate-400">{u.id}</span>
                <StatusBadge statusKey={u.status} label={tf(sm)} color={sm.color} />
              </div>
              <h3 className="font-display font-bold text-slate-900 mt-2 leading-snug">{u.title}</h3>
              <p className="text-xs text-indigo-500 mt-0.5">{tf(fnMeta(u.function))}</p>
              <div className="mt-4 space-y-2">
                <MiniStat label={t('value')} value={u.value} color="#6366F1" />
                <MiniStat label={t('absorption')} value={u.absorption} color="#EC4899" />
                <MiniStat label={t('readinessLabel')} value={u.readiness} color="#10B981" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg p-6 animate-fade-up max-h-[90vh] overflow-auto">
            <button onClick={() => setSelected(null)}
              className="absolute top-4 end-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <span className="text-xs font-mono text-slate-400">{selected.id}</span>
            <h2 className="font-display text-xl font-extrabold text-slate-900 mt-1">{selected.title}</h2>
            <div className="flex items-center gap-2 mt-2">
              <StatusBadge statusKey={selected.status} label={tf(statusMeta(selected.status))} color={statusMeta(selected.status).color} />
              <span className="text-xs text-indigo-500">{tf(fnMeta(selected.function))}</span>
            </div>
            <p className="text-sm text-slate-600 mt-4 leading-relaxed">{selected.description}</p>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <InfoCell label={t('annualValue')} value={`$${(selected.annualValue / 1000).toFixed(0)}K`} />
              <InfoCell label={t('owner')} value={selected.owner} />
            </div>
            <h4 className="font-display font-bold text-slate-900 mt-6 mb-3 text-sm">{t('fiveLayerReadiness')}</h4>
            <div className="space-y-3">
              {Object.entries(selected.layers).map(([k, v]) => {
                const colors = { technology: '#6366F1', data: '#8B5CF6', process: '#EC4899', behaviour: '#F59E0B', governance: '#10B981' };
                const names = { technology: t('overview'), data: 'Data', process: 'Process', behaviour: 'Behaviour', governance: 'Governance' };
                const layerLabels = { technology: 'Technology', data: 'Data', process: 'Process', behaviour: 'Behaviour', governance: 'Governance' };
                return <BarRow key={k} label={layerLabels[k]} value={v} color={colors[k]} suffix="%" />;
              })}
            </div>
            <button onClick={() => setSelected(null)}
              className="mt-6 w-full h-11 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors">
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 text-xs text-slate-500">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
      </div>
      <span className="w-8 text-end text-xs font-semibold text-slate-700">{value}</span>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="font-display font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
